const _excluded = ["autoSelect"];
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { fireEvent, findAllByText, findByText, waitFor } from '@testing-library/dom';

/** Simulate user events on react-select dropdowns */

function getReactSelectContainerFromInput(input) {
  return input.parentNode.parentNode.parentNode.parentNode.parentNode;
}
/**
 * Utility for opening the select's dropdown menu.
 * @param {HTMLElement} input The input field (eg. `getByLabelText('The label')`)
 */

const openMenu = input => {
  fireEvent.focus(input);
  fireEvent.keyDown(input, {
    key: "ArrowDown",
    keyCode: 40,
    code: 40
  });
}; // type text in the input field

const type = (input, text) => {
  fireEvent.change(input, {
    target: {
      value: text
    }
  });
}; // press the "clear" button, and reset various states

const clear = async (input, clearButton) => {
  fireEvent.mouseDown(clearButton);
  fireEvent.click(clearButton); // react-select will prevent the menu from opening, and asynchronously focus the select field...

  await waitFor(() => {});
  input.blur();
};

/**
 * Utility for selecting a value in a `react-select` dropdown.
 * @param {HTMLElement} input The input field (eg. `getByLabelText('The label')`)
 * @param {String|RegExp|String[]|RegExp[]} optionOrOptions The display name(s) for the option(s) to select
 * @param {Object} config Optional config options
 * @param {HTMLElement} config.container A container for the react-select and its dropdown (defaults to the react-select container)
 *                         Useful when rending the dropdown to a portal using react-select's `menuPortalTarget`
 */
const select = async function (input, optionOrOptions) {
  let config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const options = Array.isArray(optionOrOptions) ? optionOrOptions : [optionOrOptions];
  const container = config.container || getReactSelectContainerFromInput(input); // Select the items we care about

  for (const option of options) {
    openMenu(input); // only consider visible, interactive elements

    const matchingElements = await findAllByText(container, option, {
      // @ts-ignore invalid rtl types :'(
      ignore: "[aria-live] *,[style*='visibility: hidden']"
    }); // When the target option is already selected, the react-select display text
    // will also match the selector. In this case, the actual dropdown element is
    // positionned last in the DOM tree.

    const optionElement = matchingElements[matchingElements.length - 1];
    fireEvent.click(optionElement);
  }
};

/**
 * Utility for creating and selecting a value in a Creatable `react-select` dropdown.
 * @async
 * @param {HTMLElement} input The input field (eg. `getByLabelText('The label')`)
 * @param {String} option The display name for the option to type and select
 * @param {Object} config Optional config options
 * @param {HTMLElement} config.container A container for the react-select and its dropdown (defaults to the react-select container)
 *                         Useful when rending the dropdown to a portal using react-select's `menuPortalTarget`
 * @param {boolean} config.autoSelect Whether to automatically select the newly created option or not
 * @param {String|RegExp} config.createOptionText Custom label for the "create new ..." option in the menu (string or regexp)
 */
const create = async function (input, option) {
  let _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let _ref$autoSelect = _ref.autoSelect,
    autoSelect = _ref$autoSelect === void 0 ? true : _ref$autoSelect,
    config = _objectWithoutProperties(_ref, _excluded);
  const createOptionText = config.createOptionText || /^Create "/;
  openMenu(input);
  type(input, option);
  fireEvent.change(input, {
    target: {
      value: option
    }
  });
  if (autoSelect) {
    await select(input, createOptionText, config);
    await findByText(getReactSelectContainerFromInput(input), option);
  }
};
/**
 * Utility for clearing the first value of a `react-select` dropdown.
 * @param {HTMLElement} input The input field (eg. `getByLabelText('The label')`)
 */

const clearFirst = async input => {
  const container = getReactSelectContainerFromInput(input); // The "clear" button is the first svg element that is hidden to screen readers

  const clearButton = container.querySelector('svg[aria-hidden="true"]');
  await clear(input, clearButton);
};
/**
 * Utility for clearing all values in a `react-select` dropdown.
 * @param {HTMLElement} input The input field (eg. `getByLabelText('The label')`)
 */

const clearAll = async input => {
  const container = getReactSelectContainerFromInput(input); // The "clear all" button is the penultimate svg element that is hidden to screen readers
  // (the last one is the dropdown arrow)

  const elements = container.querySelectorAll('svg[aria-hidden="true"]');
  const clearAllButton = elements[elements.length - 2];
  await clear(input, clearAllButton);
};
const selectEvent = {
  select,
  create,
  clearFirst,
  clearAll,
  openMenu
};
export default selectEvent;
export { clearAll, clearFirst, create, openMenu, select };